import { useEffect, useMemo, useState } from 'react';
import { Box, HStack, VStack, Image, useMediaQuery } from 'native-base';
import { find } from 'lodash';

import { Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { KitchenWeeklyGoals, useKitchenPositionGuideLive } from '@app/features/kitchen-positioning';
import { GoalTypesSelection } from '@app/features/ops-plan';
import { useGoalTypes } from '@app/features/store-info';
import { AggGoalTypeDto } from '@pimm/services/lib/sms-workforce';

type KitchenGoalsProps = React.ComponentProps<typeof VStack> & {
  onEditGoals?: () => void;
};

export const KitchenGoals = (props: KitchenGoalsProps) => {
  const { locale, translate } = useAppLocale();
  const goalTypes = useGoalTypes();
  const [isSmallScreen] = useMediaQuery({ maxWidth: 1200 });
  const livePositionGuide = useKitchenPositionGuideLive();
  const weeklyFocusSetting = livePositionGuide.data?.weeklyFocusSetting;
  const [osat, setOsat] = useState<AggGoalTypeDto>();

  const localizedHeaderImg = useMemo(
    () => weeklyFocusSetting?.headerImgAlt?.replace(/_[a-z]{2}-[a-z]{2}(?=\.\w+$)/i, `_${locale?.toLowerCase()}`),
    [weeklyFocusSetting?.headerImgAlt, locale],
  );

  useEffect(() => {
    setOsat(find(goalTypes.data, ['seq', 0]));
  }, [goalTypes.data]);

  return (
    <VStack h="full">
      {livePositionGuide.isSuccess && (
        <>
          <HStack
            space={{ xl: 1 }}
            borderBottomWidth={1}
            bg={isSmallScreen ? 'white' : undefined}
            alignItems={isSmallScreen ? 'center' : 'flex-start'}
            flexDirection={isSmallScreen ? 'row' : 'column'}
          >
            {!!localizedHeaderImg && (
              <Box background="white" borderBottomWidth={isSmallScreen ? 0 : 1} minH="150px" width={isSmallScreen ? 400 : '100%'}>
                <Image
                  source={{ uri: localizedHeaderImg }}
                  style={{
                    width: isSmallScreen ? 400 : '100%',
                    height: isSmallScreen ? 150 : '100%',
                  }}
                  resizeMode="contain"
                  alt={translate(weeklyFocusSetting?.headerImgCaption, weeklyFocusSetting?.headerImgCaptionTranslations)}
                />
              </Box>
            )}

            <VStack w="full" flex={1} alignItems={isSmallScreen ? 'flex-end' : 'flex-start'} px={3} py={isSmallScreen ? 0 : 2} space={2}>
              <HStack w="full" alignItems="center" justifyContent="space-between" space={isSmallScreen ? 0 : 2}>
                <Text size={{ md: 'xl', xl: '2xl' }} fontWeight={700} color="black" lineHeight="xs" numberOfLines={2} ellipsizeMode="tail">
                  {translate(weeklyFocusSetting?.title, weeklyFocusSetting?.translations)}
                </Text>

                {!isSmallScreen && (
                  <Text size="xl" fontWeight={700} color="blue.600" lineHeight="xs">
                    {`${translate(osat?.title, osat?.translations)} ${osat?.score ?? 0}%`}
                  </Text>
                )}
              </HStack>

              <HStack w="full" space={1.5} alignItems="center">
                {isSmallScreen && (
                  <VStack
                    space={1}
                    rounded="lg"
                    alignItems="center"
                    justifyContent="center"
                    w={90}
                    h="58px"
                    borderWidth={2}
                    borderColor="blueLight.500"
                    bg="blueLight.100"
                  >
                    <Text size="sm" fontWeight={700} color="blue.500" lineHeight="xs">
                      {translate(osat?.title, osat?.translations)}
                    </Text>
                    <Text size="lg" fontWeight={700} color="blue.500" lineHeight="xs">
                      {`${osat?.score ?? 0}%`}
                    </Text>
                  </VStack>
                )}

                <GoalTypesSelection
                  _container={{
                    minW: isSmallScreen ? { md: 450, xl: 'auto' } : undefined,
                    h: isSmallScreen ? '58px' : undefined,
                    flex: isSmallScreen ? 1 : undefined,
                  }}
                  _icon={{ color: 'black', size: 4 }}
                  showTitle={isSmallScreen}
                  disabled
                  goalTypes={goalTypes.data}
                  goals={weeklyFocusSetting?.goalTypeIds}
                />
              </HStack>

              <Text size="md" color="gray.700" fontWeight={500}>
                {translate(weeklyFocusSetting?.subHeaderCaption, weeklyFocusSetting?.subHeaderCaptionTranslations)}
              </Text>
            </VStack>
          </HStack>

          <Box flex={1} px={1}>
            <KitchenWeeklyGoals categories={livePositionGuide.data?.weeklyFocusSetting?.categories} onEditGoals={props.onEditGoals} />
          </Box>
        </>
      )}
    </VStack>
  );
};
